/**
 * SYSTEM_ADMIN — Drift opt-out overview — Cycle 23.
 *
 * 1. institution-wide 차단 list (1명의 ADMIN 이 institution 전체 inbox 차단)
 * 2. 본인 opt-out 통계 (institution 별 count + 최근 변경)
 * 3. 총합
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import {
  getDriftOptOutOverview, getDriftOptOutHistory, getDriftOptOutDistribution,
  type DriftOptOutOverview, type DriftOptOutHistoryItem, type DriftOptOutDistDay,
} from '../../api/systemAdmin';
// Cycle 28 — dashboard i18n
// Cycle 39 — subscribeDriftLang 으로 lang switcher 따라 자동 re-render.
// Cycle 43 — getDateLocale / getNumberLocale 으로 lang-aware date/number 형식
import { t, subscribeDriftLang, getDriftLang } from '../../lib/drift-i18n';
import { getNumberLocale, formatDateTime } from '../../lib/base-i18n';
import '../AdminConsolePage/AdminConsolePage.css';

export function DriftOptOutDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<DriftOptOutOverview | null>(null);
  const [history, setHistory] = useState<DriftOptOutHistoryItem[]>([]);
  const [series, setSeries] = useState<DriftOptOutDistDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cycle 39 — lang 변경 시 re-render. getDriftLang 으로 초기값 확인.
  const [, setLangBump] = useState(getDriftLang());
  useEffect(() => subscribeDriftLang(() => setLangBump(getDriftLang())), []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [overview, hist, dist] = await Promise.all([
        getDriftOptOutOverview(),
        getDriftOptOutHistory({ limit: 30 }).catch(() => ({ items: [], limit: 30 })),
        getDriftOptOutDistribution({ days: 30 }).catch(() => ({ days: 30, series: [] })),
      ]);
      setData(overview);
      setHistory(hist.items);
      setSeries(dist.series);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t('driftdash.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  const actionLabel: Record<DriftOptOutHistoryItem['action'], string> = {
    opt_out: t('driftdash.action.opt_out'),
    opt_in: t('driftdash.action.opt_in'),
    inst_opt_out: t('driftdash.action.inst_opt_out'),
    inst_opt_in: t('driftdash.action.inst_opt_in'),
  };

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <div className="admin-console admin-console--denied"><h2>{t('driftdash.access_denied')}</h2></div>;
  }

  return (
    <div className="admin-console">
      <aside className="admin-console__sidebar">
        <Link to="/system-admin" className="admin-console__btn" style={{ width: '100%', textDecoration: 'none', textAlign: 'center', display: 'block' }}>
          {t('driftdash.back_link')}
        </Link>
      </aside>
      <main className="admin-console__content">
        <h2 className="admin-console__heading">{t('driftdash.heading')}</h2>
        <p className="admin-console__subheading">{t('driftdash.subheading')}</p>

        {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
        {loading && <p>{t('drift.loading')}</p>}

        {!loading && data && (
          <>
            <section style={{ marginBottom: '1.5rem' }} aria-label="summary">
              <div style={{ display: 'flex', gap: '1rem' }}>
                <SummaryCard label={t('driftdash.summary.inst_disabled')} value={data.totalInstitutionsDisabled} />
                <SummaryCard label={t('driftdash.summary.user_total')} value={data.totalUsersOptedOut} />
              </div>
            </section>

            <section style={{ marginBottom: '2rem' }} aria-label="institution-wide list">
              <h3>{t('driftdash.inst_list.heading')} ({data.institutionsDisabled.length})</h3>
              {data.institutionsDisabled.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary, #6b7280)' }}>{t('driftdash.inst_list.empty')}</p>
              ) : (
                <table className="admin-console__table">
                  <thead><tr><th>{t('driftdash.col.company')}</th><th>{t('driftdash.col.domain')}</th><th>{t('driftdash.col.detail')}</th></tr></thead>
                  <tbody>
                    {data.institutionsDisabled.map((it) => (
                      <tr key={it.institutionId}>
                        <td><strong>{it.name}</strong></td>
                        <td>{it.domain ?? '—'}</td>
                        <td>
                          <Link to={`/system-admin/tenants/${it.institutionId}`}>{t('driftdash.tenant_link')}</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {series.length > 0 && (
              <section style={{ marginBottom: '2rem' }} aria-label="distribution chart">
                <h3>{t('driftdash.chart.heading')}</h3>
                <DistributionChart series={series} />
              </section>
            )}

            {/* Cycle 34 — 일자별 누적 (running total) line chart */}
            {series.length > 1 && (
              <section style={{ marginBottom: '2rem' }} aria-label="cumulative trend chart">
                <h3>{t('driftdash.cumchart.heading')}</h3>
                <CumulativeTrendChart series={series} />
              </section>
            )}

            <section style={{ marginBottom: '2rem' }} aria-label="change history">
              <h3>{t('driftdash.history.heading')} ({history.length})</h3>
              {history.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary, #6b7280)' }}>{t('driftdash.history.empty')}</p>
              ) : (
                <table className="admin-console__table">
                  <thead>
                    <tr>
                      <th>{t('driftdash.col.time')}</th>
                      <th>{t('driftdash.col.scope')}</th>
                      <th>{t('driftdash.col.company')}</th>
                      <th>{t('driftdash.col.action')}</th>
                      <th>{t('driftdash.col.actor')}</th>
                      <th>{t('driftdash.col.reason_masked')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td style={{ fontSize: '0.8125rem' }}>{formatDateTime(h.createdAt)}</td>
                        <td>{h.scope === 'institution' ? t('driftdash.scope.institution') : t('driftdash.scope.user')}</td>
                        <td>
                          <Link to={`/system-admin/tenants/${h.institutionId}`}>{h.institutionName}</Link>
                        </td>
                        <td>
                          <code style={{ fontSize: '0.8125rem' }}>{actionLabel[h.action]}</code>
                          {h.piiDetected && (
                            <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: 'var(--color-warn, #d97706)' }}>
                              ⚠ PII
                            </span>
                          )}
                        </td>
                        <td>{h.actorId}</td>
                        <td style={{ fontSize: '0.8125rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {h.reasonMasked ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section aria-label="user stats">
              <h3>{t('driftdash.user_stats.heading')} ({data.userOptOutsByInstitution.length})</h3>
              {data.userOptOutsByInstitution.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary, #6b7280)' }}>{t('driftdash.user_stats.empty')}</p>
              ) : (
                <table className="admin-console__table">
                  <thead>
                    <tr>
                      <th>{t('driftdash.col.company')}</th>
                      <th>{t('driftdash.col.count')}</th>
                      <th>{t('driftdash.col.latest_change')}</th>
                      <th>{t('driftdash.col.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.userOptOutsByInstitution.map((it) => (
                      <tr key={it.institutionId}>
                        <td><strong>{it.name}</strong></td>
                        <td>{it.count.toLocaleString(getNumberLocale())}</td>
                        <td style={{ fontSize: '0.8125rem' }}>
                          {it.latestUpdatedAt ? formatDateTime(it.latestUpdatedAt) : '—'}
                        </td>
                        <td>
                          <Link to={`/system-admin/tenants/${it.institutionId}`}>{t('driftdash.tenant_link')}</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

// Cycle 26 — 일자별 stacked bar chart. CSS flexbox + div height % — 외부 차트 라이브러리 의존 0.
function DistributionChart({ series }: { series: DriftOptOutDistDay[] }) {
  const colors = {
    opt_out: 'var(--color-brand, #4f46e5)',
    opt_in: 'var(--color-success, #15803d)',
    inst_opt_out: 'var(--color-danger, #b91c1c)',
    inst_opt_in: 'var(--color-warn, #d97706)',
  };
  const max = Math.max(
    1,
    ...series.map((d) => d.opt_out + d.opt_in + d.inst_opt_out + d.inst_opt_in),
  );

  return (
    <div>
      <div
        role="img"
        aria-label="일자별 drift opt-out 변경 분포"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '3px',
          height: '160px',
          borderBottom: '1px solid var(--color-border, #e5e7eb)',
          padding: '0 0.25rem',
        }}
      >
        {series.map((d) => {
          const total = d.opt_out + d.opt_in + d.inst_opt_out + d.inst_opt_in;
          return (
            <div
              key={d.day}
              data-testid={`dist-bar-${d.day}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
              title={`${d.day}: opt_out ${d.opt_out} / opt_in ${d.opt_in} / inst_out ${d.inst_opt_out} / inst_in ${d.inst_opt_in}`}
            >
              <div style={{ width: '100%', height: `${(total / max) * 100}%`, display: 'flex', flexDirection: 'column-reverse', minHeight: total > 0 ? '4px' : '0' }}>
                {d.opt_out > 0 && <div style={{ flex: d.opt_out, background: colors.opt_out }} />}
                {d.opt_in > 0 && <div style={{ flex: d.opt_in, background: colors.opt_in }} />}
                {d.inst_opt_out > 0 && <div style={{ flex: d.inst_opt_out, background: colors.inst_opt_out }} />}
                {d.inst_opt_in > 0 && <div style={{ flex: d.inst_opt_in, background: colors.inst_opt_in }} />}
              </div>
              <span style={{ fontSize: '0.625rem', color: 'var(--color-text-secondary, #6b7280)' }}>
                {d.day.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      {/* legend */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.75rem', flexWrap: 'wrap' }}>
        <LegendChip color={colors.opt_out} label={t('driftdash.legend.opt_out')} />
        <LegendChip color={colors.opt_in} label={t('driftdash.legend.opt_in')} />
        <LegendChip color={colors.inst_opt_out} label={t('driftdash.legend.inst_opt_out')} />
        <LegendChip color={colors.inst_opt_in} label={t('driftdash.legend.inst_opt_in')} />
      </div>
    </div>
  );
}

// Cycle 34 — 일자별 누적 (running total) line chart. SVG path 자체 작성, 외부 lib 0.
//
// 두 series 의 cumulative sum 을 SVG polyline 으로:
//   - personal: opt_out - opt_in 의 cumulative sum (=현재 시점 본인 opt-out 사용자 net 변화)
//   - institution: inst_opt_out - inst_opt_in 의 cumulative sum
// 양 series 시작점 0 → 마지막 점이 net delta. cycle 26 의 byDay distribution 의 시간순 보강.
//
// Cycle 35 — data point circle + hover tooltip (mouseenter/mouseleave + state). 외부 lib 0.
function CumulativeTrendChart({ series }: { series: DriftOptOutDistDay[] }) {
  const sorted = [...series].sort((a, b) => a.day.localeCompare(b.day));
  // 누적
  let personalCum = 0, instCum = 0;
  const points = sorted.map((d) => {
    personalCum += (d.opt_out - d.opt_in);
    instCum += (d.inst_opt_out - d.inst_opt_in);
    return { day: d.day, personal: personalCum, institution: instCum };
  });

  // Cycle 48 adversarial S1 봉합 — 빈 데이터 시 origin point collapse 차트 (사용자가
  // "정상 데이터 0%" 로 오독) 차단. 명시적 placeholder 렌더 + chart 자체 skip.
  if (points.length === 0) {
    return (
      <div
        data-testid="cum-chart-empty"
        style={{
          padding: '1rem',
          textAlign: 'center',
          color: 'var(--color-text-secondary, #6b7280)',
          fontSize: '0.875rem',
          border: '1px dashed var(--color-border, #e5e7eb)',
          borderRadius: '4px',
        }}
      >
        {t('driftdash.history.empty')}
      </div>
    );
  }

  // SVG viewBox 좌표 — width 600 height 160 (responsive)
  const W = 600;
  const H = 160;
  const padX = 40;
  const padY = 20;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  const xs = points.map((_, i) => padX + (i / Math.max(1, points.length - 1)) * innerW);
  const allYs = [...points.map((p) => p.personal), ...points.map((p) => p.institution), 0];
  const yMin = Math.min(...allYs);
  const yMax = Math.max(...allYs, 1);
  const range = yMax - yMin || 1;
  function yPos(v: number) { return padY + innerH - ((v - yMin) / range) * innerH; }

  const personalPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(xs[i] ?? 0).toFixed(1)} ${yPos(p.personal).toFixed(1)}`).join(' ');
  const instPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${(xs[i] ?? 0).toFixed(1)} ${yPos(p.institution).toFixed(1)}`).join(' ');

  // Cycle 35 — hover state: 어떤 point index 위에 있는가.
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hoverPoint = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="cumulative drift opt-out trend"
        data-testid="cumulative-trend-svg"
        style={{ width: '100%', height: 'auto', maxWidth: `${W}px` }}
      >
        {/* axis */}
        <line x1={padX} y1={padY} x2={padX} y2={H - padY} stroke="var(--color-border, #e5e7eb)" />
        <line x1={padX} y1={H - padY} x2={W - padX} y2={H - padY} stroke="var(--color-border, #e5e7eb)" />
        {/* zero line if min < 0 */}
        {yMin < 0 && (
          <line x1={padX} y1={yPos(0)} x2={W - padX} y2={yPos(0)} stroke="var(--color-text-secondary, #d1d5db)" strokeDasharray="2 4" />
        )}
        {/* paths */}
        <path d={personalPath} fill="none" stroke="var(--color-brand, #4f46e5)" strokeWidth="2" />
        <path d={instPath} fill="none" stroke="var(--color-danger, #b91c1c)" strokeWidth="2" />

        {/* Cycle 35 — data point circles (hover target).
            invisible 큰 hit-area + 작은 visible dot.
            personal/institution 각 point 의 hit-area 는 동일 x → mouseenter index 로 통합.
            Cycle 36 — 키보드 a11y: hit-area rect 가 button role + tabindex=0 + onFocus/Blur
            (tooltip 고정) + Enter/Space 토글. Tab 으로 point 순회 가능. aria-label 에 일자/값. */}
        {points.map((p, i) => {
          const ariaLabel = `${p.day}: ${t('driftdash.cumchart.personal_net')} ${p.personal}, ${t('driftdash.cumchart.institution_net')} ${p.institution}`;
          return (
            <g key={p.day} data-testid={`cum-point-${p.day}`}>
              {/* visible dot — personal */}
              <circle
                cx={xs[i]}
                cy={yPos(p.personal)}
                r={hoverIndex === i ? 4 : 2.5}
                fill="var(--color-brand, #4f46e5)"
              />
              {/* visible dot — institution */}
              <circle
                cx={xs[i]}
                cy={yPos(p.institution)}
                r={hoverIndex === i ? 4 : 2.5}
                fill="var(--color-danger, #b91c1c)"
              />
              {/* invisible hit-area — column 전체 (mouseenter + keyboard focus 안정).
                  Cycle 37 — :focus-visible CSS class (.cum-hit-area) 추가. 키보드 진입 시만
                  stroke ring 표시 (WCAG 2.4.7). mouse click 시는 stroke 없음. */}
              <rect
                x={(xs[i] ?? 0) - innerW / Math.max(1, points.length * 2)}
                y={padY}
                width={innerW / Math.max(1, points.length)}
                height={innerH}
                fill="transparent"
                className="cum-hit-area"
                data-testid={`cum-hit-${p.day}`}
                role="button"
                tabIndex={0}
                aria-label={ariaLabel}
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((cur) => (cur === i ? null : cur))}
                onFocus={() => setHoverIndex(i)}
                onBlur={() => setHoverIndex((cur) => (cur === i ? null : cur))}
                onKeyDown={(e) => {
                  // Enter/Space → tooltip toggle (focus 받으면 이미 hoverIndex 설정됨 — 토글로 dismiss 가능)
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setHoverIndex((cur) => (cur === i ? null : i));
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            </g>
          );
        })}

        {/* y-axis tick — min/max */}
        <text x={padX - 4} y={padY + 4} textAnchor="end" fontSize="9" fill="var(--color-text-secondary, #6b7280)">{yMax}</text>
        <text x={padX - 4} y={H - padY} textAnchor="end" fontSize="9" fill="var(--color-text-secondary, #6b7280)">{yMin}</text>
        {/* x-axis labels — 첫/마지막 */}
        <text x={padX} y={H - padY + 12} textAnchor="middle" fontSize="9" fill="var(--color-text-secondary, #6b7280)">{points[0]?.day.slice(5)}</text>
        <text x={W - padX} y={H - padY + 12} textAnchor="middle" fontSize="9" fill="var(--color-text-secondary, #6b7280)">{points[points.length - 1]?.day.slice(5)}</text>
      </svg>

      {/* Cycle 35 — hover tooltip (HTML, SVG 외부). SVG 내부 foreignObject 회피 (test 친화). */}
      {hoverPoint && (
        <div
          data-testid="cum-tooltip"
          role="tooltip"
          style={{
            position: 'absolute',
            top: '0.25rem',
            right: '0.25rem',
            background: 'var(--color-surface, #fff)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: '4px',
            padding: '0.375rem 0.625rem',
            fontSize: '0.75rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
            pointerEvents: 'none',
            minWidth: '160px',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{hoverPoint.day}</div>
          <div>
            <span style={{ color: 'var(--color-brand, #4f46e5)' }}>● </span>
            {t('driftdash.cumchart.personal_net')}: <strong>{hoverPoint.personal}</strong>
          </div>
          <div>
            <span style={{ color: 'var(--color-danger, #b91c1c)' }}>● </span>
            {t('driftdash.cumchart.institution_net')}: <strong>{hoverPoint.institution}</strong>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', marginTop: '0.5rem' }}>
        <LegendChip color="var(--color-brand, #4f46e5)" label={t('driftdash.cumchart.personal_net')} />
        <LegendChip color="var(--color-danger, #b91c1c)" label={t('driftdash.cumchart.institution_net')} />
      </div>
    </div>
  );
}

function LegendChip({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
      <span style={{ width: '10px', height: '10px', background: color, borderRadius: '2px', display: 'inline-block' }} />
      {label}
    </span>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '1rem',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: '6px',
        background: 'var(--color-surface, #fff)',
      }}
    >
      <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 600, marginTop: '0.25rem' }}>{value.toLocaleString(getNumberLocale())}</div>
    </div>
  );
}
